import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, ArrowRight, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export default function OrderSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const { axiosInstance } = useAuth();
  const { fetchCart } = useCart();
  
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId && !orderId) {
        setStatus('error');
        return;
      }

      try {
        if (sessionId) {
          // Poll Stripe status
          const response = await axiosInstance.get(`/payments/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            await fetchCart();
          } else if (response.data.status === 'expired') {
            setStatus('expired');
          } else if (attempts < 5) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus('pending');
          }
        } else if (orderId) {
          // Get order details
          const response = await axiosInstance.get(`/orders/${orderId}`);
          setOrder(response.data);
          setStatus('success');
          await fetchCart();
        }
      } catch (error) {
        console.error('Error checking status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, orderId, attempts, axiosInstance, fetchCart]);

  const statusContent = {
    loading: {
      icon: <Clock className="text-ocre animate-pulse" size={60} />,
      title: 'Vérification de la commande...',
      description: 'Merci de patienter quelques instants.',
      color: 'text-ocre',
    },
    success: {
      icon: <CheckCircle className="text-sage" size={60} />,
      title: 'Commande confirmée !',
      description: 'Votre commande a été enregistrée avec succès.',
      color: 'text-sage',
    },
    pending: {
      icon: <Clock className="text-ocre" size={60} />,
      title: 'Paiement en attente',
      description: 'Veuillez finaliser votre paiement pour confirmer la commande.',
      color: 'text-ocre',
    },
    expired: {
      icon: <XCircle className="text-terracotta" size={60} />,
      title: 'Session expirée',
      description: 'La session de paiement a expiré. Veuillez réessayer.',
      color: 'text-terracotta',
    },
    error: {
      icon: <XCircle className="text-terracotta" size={60} />,
      title: 'Une erreur est survenue',
      description: 'Impossible de vérifier le statut de votre commande.',
      color: 'text-terracotta',
    },
  };

  const content = statusContent[status];

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      <div className="container-padding max-w-2xl mx-auto py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 shadow-card text-center"
        >
          <div className="mb-6">{content.icon}</div>
          
          <h1 className={`font-heading text-3xl font-bold ${content.color} mb-3`}>
            {content.title}
          </h1>
          
          <p className="text-navy/70 mb-8">{content.description}</p>

          {status === 'success' && (
            <div className="bg-sage/10 rounded-2xl p-6 mb-8 text-left">
              <div className="flex items-center gap-3 mb-4">
                <Package className="text-sage" size={24} />
                <span className="font-semibold text-navy">Détails de la commande</span>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-navy/70">
                  Le paiement s'effectuera par terminal (carte ou Twint) lors de la livraison.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {status === 'success' && (
              <Link
                to="/account"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="view-orders-button"
              >
                Voir mes commandes
                <ArrowRight size={20} />
              </Link>
            )}
            
            <Link
              to="/menu"
              className={status === 'success' ? 'btn-secondary' : 'btn-primary'}
              data-testid="back-to-menu-button"
            >
              {status === 'success' ? 'Retour à la carte' : 'Commander à nouveau'}
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
